import ReactMarkdown from 'react-markdown';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'src/store/store';
import Button from 'src/components/buttons/button';
import i18n from 'src/utils/translate';
import { ExternalLink, BlockQuote, HtmlNode } from 'src/modules/programs/components/markdownRenderers';
import { enrollUser, markSectionAsComplete } from 'src/modules/programs/actions';
import { showSignupModal, showModal } from 'src/modules/shared/modal/actions';
import { ModalType } from 'src/constants/modalTypes';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Section } from 'src/constants/footfall';
import { markdownStyles } from 'styles-js/markdown';
import Icon from 'src/components/icon';

type Props = {
  section: {
    programSectionId: number;
    title: string;
    content: string;
  };
  isCompleted: boolean;
  moduleCode: string;
}

export default function SectionContent({
  section,
  isCompleted,
  moduleCode,
}: Props) {
  const dispatch = useDispatch();

  const currentUser = useSelector(({ context }) => context.currentUser);
  const isEnrolled = useSelector(({ pages }) => pages.programs.isEnrolled);

  const renderers = {
    blockquote: BlockQuote,
    link: ExternalLink,
    html: HtmlNode,
  };

  const onButtonClick = async () => {
    if (!currentUser) {
      const onSignupSuccess = async () => {
        trackFootfallEvent(Event.Clicked, { clickType: 'program', clickMetadata: { section: Section.MarkSectionAsComplete, programSectionId: section.programSectionId } });
        dispatch(enrollUser());
        dispatch(markSectionAsComplete(section.programSectionId, moduleCode));
      };
      dispatch(showSignupModal({ onSignupSuccess }));
    } else if (!isEnrolled) {
      dispatch(showModal({
        modalType: ModalType.ProgramEnroll,
        modalProps: {
          programSectionId: section.programSectionId,
          moduleCode,
        },
      }));
    } else {
      trackFootfallEvent(Event.Clicked, { clickType: 'program', clickMetadata: { section: Section.MarkSectionAsComplete, programSectionId: section.programSectionId } });
      dispatch(markSectionAsComplete(section.programSectionId, moduleCode));
    }
  };

  return (
    <MarkdownContainer key={section.programSectionId}>
      <h3>{section.title}</h3>
      <ReactMarkdown
        escapeHtml={false}
        renderers={renderers}
        source={section.content}
      />
      <ButtonContainer>
        {!isCompleted
          ? (
            <Button onClick={onButtonClick}>
              {i18n.t('Mark as complete')}
            </Button>
          ) : (
            <SectionComplete>
              {i18n.t('Completed')} <Icon colour="colorGreen" icon="ok" />
            </SectionComplete>
          )
        }
      </ButtonContainer>
    </MarkdownContainer>
  );
}

const MarkdownContainer = styled.div`
  ${markdownStyles};
  padding-bottom: 20px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SectionComplete = styled.div`
  border: solid 1px ${props => props.theme.colorGreyLight};
  padding: 6px 12px;
  margin: 5px 5px 19px 0;
  border-radius: .25rem;
`;
